/**
 * Ambient signature re-weave — deterministic fluid-zone reorder using local pool metadata.
 * Committed zone (current + next 2) is never touched. LLM depth refresh runs in background only.
 */

import { useMaestroStore } from '../store/maestroStore';
import { QueueController } from '../transport/QueueController';
import { smartPlaylistService, type CompactPoolTrack } from '../services/smartPlaylistService';
import { ellaTracksToQueueFormat } from './ellaQueueFormat';
import {
  buildAmbientSignature,
  fetchFreshEllaContinueOpts,
  type AmbientSliders,
  applyAmbientSlidersToTuning,
} from './ellaRadioAmbient';
import { ellaRadioSessionRefs } from './ellaRadioSessionRefs';
import { isRadioRefillActive, fetchRadioHistory } from './radioRefill';
import { bankReserveTracks } from './radioReserve';

/** Tracks after current + next 2 — safe to reorder/replace. */
export const COMMITTED_TAIL = 2;

const REWEAVE_COOLDOWN_MS = 10 * 60_000;
const AMBIENT_POLL_MS = 5 * 60_000;

let _poolSlice: CompactPoolTrack[] = [];
let _snapshotStack: Array<{ fluidTrackIds: string[]; signature: string }> = [];
let _lastShiftReweaveAt = 0;
let _lastRevertReweaveAt = 0;
let _ambientPollTimer: ReturnType<typeof setInterval> | null = null;
let _lastSignature = '';

export function setLocalPoolSlice(slice: CompactPoolTrack[] | undefined | null): void {
  _poolSlice = Array.isArray(slice) ? slice : [];
}

export function getLocalPoolSlice(): CompactPoolTrack[] {
  return _poolSlice;
}

export function resetAmbientReweaveState(): void {
  _poolSlice = [];
  _snapshotStack = [];
  _lastShiftReweaveAt = 0;
  _lastRevertReweaveAt = 0;
  _lastSignature = '';
  stopAmbientPoll();
}

export function startAmbientPoll(): void {
  stopAmbientPoll();
  if (!isRadioRefillActive()) return;
  void _pollAmbientReweave(false);
  _ambientPollTimer = setInterval(() => {
    void _pollAmbientReweave(false);
  }, AMBIENT_POLL_MS);
}

export function stopAmbientPoll(): void {
  if (_ambientPollTimer) {
    clearInterval(_ambientPollTimer);
    _ambientPollTimer = null;
  }
}

function _ambientAware(): boolean {
  return ellaRadioSessionRefs.timeAware || ellaRadioSessionRefs.weatherAware;
}

function _trackKey(track: any): string {
  return String(track?.id || track?.qobuzTrackId || track?.trackId || '');
}

function _committedEndIndex(queueIndex: number): number {
  return queueIndex + 1 + COMMITTED_TAIL;
}

function _scorePoolTrack(
  entry: CompactPoolTrack,
  targetEnergy: number,
  playedIds: Set<string>,
  queuedIds: Set<string>,
): number {
  const id = entry.id ? String(entry.id) : `${entry.artist}|${entry.title}`;
  if (playedIds.has(id)) return -1;
  if (queuedIds.has(id)) return -1;
  const energy = entry.energy ?? 50;
  const energyDist = Math.abs(energy - targetEnergy);
  const popBoost = (entry.pop ?? 30) * 0.15;
  return 100 - energyDist + popBoost;
}

function _deterministicReweave(
  fluidTracks: any[],
  ambient: Record<string, unknown>,
  playedIds: Set<string>,
  queuedIds: Set<string>,
): any[] {
  const targetEnergy = Number(ambient.ambientEnergy ?? 50);
  const pool = _poolSlice.filter((p) => p.id);
  if (pool.length === 0) {
    return [...fluidTracks].sort((a, b) => {
      const ea = Number(a?.energyEstimate ?? a?.energy ?? 50);
      const eb = Number(b?.energyEstimate ?? b?.energy ?? 50);
      return Math.abs(ea - targetEnergy) - Math.abs(eb - targetEnergy);
    });
  }

  const rankedPool = pool
    .map((p) => ({ p, score: _scorePoolTrack(p, targetEnergy, playedIds, queuedIds) }))
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score);

  const kept: any[] = [];
  const usedIds = new Set<string>();

  for (const item of fluidTracks) {
    const t = item?.track ?? item;
    const key = _trackKey(t);
    const energy = Number(t?.energyEstimate ?? 50);
    if (Math.abs(energy - targetEnergy) <= 22) {
      kept.push(item?.track ? item : { track: t });
      if (key) usedIds.add(key);
    }
  }

  for (const { p } of rankedPool) {
    if (kept.length >= fluidTracks.length) break;
    const id = String(p.id);
    if (usedIds.has(id)) continue;
    kept.push({
      track: {
        id,
        qobuzTrackId: id,
        trackId: id,
        title: p.title,
        artist: p.artist,
        artistName: p.artist,
        energyEstimate: p.energy ?? undefined,
        source: 'qobuz',
      },
    });
    usedIds.add(id);
  }

  while (kept.length < fluidTracks.length) {
    const fallback = fluidTracks[kept.length];
    if (!fallback) break;
    kept.push(fallback?.track ? fallback : { track: fallback });
  }

  return kept.slice(0, fluidTracks.length);
}

function _ellaShiftLine(prevLabel: string, newLabel: string): string {
  return `The mood outside just shifted — I'm weaving in something that fits ${newLabel || 'this moment'}.`;
}

function _ellaRevertLine(label: string): string {
  return `Skies are settling — easing back toward what we had queued for ${label || 'calmer air'}.`;
}

async function _backgroundMomentContinue(
  fresh: Record<string, unknown>,
  prevLabel: string,
  newLabel: string,
  reason: 'weather_shift' | 'time_shift' | 'combined_shift',
): Promise<void> {
  const playlistId = ellaRadioSessionRefs.ellaPlaylistId;
  if (!playlistId) return;

  try {
    const historyIds = await fetchRadioHistory(30);
    const sessionPlayed = ellaRadioSessionRefs.getPlayedTrackIds();
    const allExcluded = [...new Set([...historyIds, ...sessionPlayed])];
    const baseTuning = ellaRadioSessionRefs.buildTuningParams();
    const sliders: AmbientSliders | null = fresh.ambientEnergy != null
      ? {
          energy: fresh.ambientEnergy as number,
          acoustic: fresh.ambientAcoustic as number,
          valence: fresh.ambientValence as number,
          vocal: fresh.ambientVocal as number,
        }
      : null;
    const tuningParams = applyAmbientSlidersToTuning(baseTuning, sliders);

    const res = await smartPlaylistService.continue(playlistId, {
      playedTrackIds: allExcluded,
      skippedTrackIds: ellaRadioSessionRefs.getSkippedTrackIds(),
      tuningParams,
      tweaks: ellaRadioSessionRefs.getTweaks(),
      ...fresh,
      momentInterrupt: true,
      momentReason: reason,
      previousAmbientLabel: prevLabel,
      newAmbientLabel: newLabel,
    });

    if (res.reserveTracks?.length) {
      bankReserveTracks(res.reserveTracks);
    }
    if (res.tracks?.length) {
      bankReserveTracks(res.tracks);
    }
    if (res.poolSlice?.length) {
      setLocalPoolSlice(res.poolSlice);
    }
    console.log('[radioReweave] background_moment_continue depth', res.tracks?.length ?? 0);
  } catch (e: any) {
    console.warn('[radioReweave] background_moment_continue failed:', e?.message || e);
  }
}

async function _applySignatureShift(
  fresh: Record<string, unknown>,
  prevOpts: Record<string, unknown>,
  reason: 'weather_shift' | 'time_shift' | 'combined_shift',
): Promise<void> {
  if (Date.now() - _lastShiftReweaveAt < REWEAVE_COOLDOWN_MS) return;
  _lastShiftReweaveAt = Date.now();

  const state = useMaestroStore.getState();
  const committedEnd = _committedEndIndex(state.queueIndex);
  const fluidItems = state.queue.slice(committedEnd);
  if (fluidItems.length === 0) return;

  const sig = buildAmbientSignature(fresh);
  _snapshotStack.push({
    fluidTrackIds: fluidItems.map((item) => _trackKey(item?.track)),
    signature: _lastSignature || buildAmbientSignature(prevOpts),
  });

  const playedIds = new Set(ellaRadioSessionRefs.getPlayedTrackIds());
  const queuedIds = new Set(
    state.queue.slice(0, committedEnd).map((item) => _trackKey(item?.track)),
  );
  const reordered = _deterministicReweave(
    fluidItems.map((i) => i.track),
    fresh,
    playedIds,
    queuedIds,
  );

  QueueController.replaceFluidZone(reordered, { committedTail: COMMITTED_TAIL });

  const prevLabel = String(prevOpts.ambientMoodLabel || prevOpts.timeOfDayLabel || prevOpts.weatherCondition || '');
  const newLabel = String(fresh.ambientMoodLabel || fresh.timeOfDayLabel || fresh.weatherCondition || '');
  ellaRadioSessionRefs.onAmbientReweave?.(_ellaShiftLine(prevLabel, newLabel), 'shift');

  console.log('[radioReweave] ambient_shift_reweave', { signature: sig, fluid: fluidItems.length });

  void _backgroundMomentContinue(fresh, prevLabel, newLabel, reason);
}

async function _applySignatureRevert(fresh: Record<string, unknown>): Promise<void> {
  if (_snapshotStack.length === 0) return;
  if (Date.now() - _lastRevertReweaveAt < REWEAVE_COOLDOWN_MS) return;
  _lastRevertReweaveAt = Date.now();

  const snap = _snapshotStack.pop()!;
  const state = useMaestroStore.getState();
  const committedEnd = _committedEndIndex(state.queueIndex);
  const fluidItems = state.queue.slice(committedEnd);
  const playedIds = new Set(ellaRadioSessionRefs.getPlayedTrackIds());

  const byId = new Map<string, any>();
  for (const item of fluidItems) {
    const key = _trackKey(item?.track);
    if (key) byId.set(key, item);
  }

  const restored: any[] = [];
  const seen = new Set<string>();
  for (const id of snap.fluidTrackIds) {
    if (!id || playedIds.has(id) || seen.has(id)) continue;
    const item = byId.get(id);
    if (item) {
      restored.push(item);
      seen.add(id);
    }
  }
  for (const item of fluidItems) {
    const key = _trackKey(item?.track);
    if (!key || seen.has(key) || playedIds.has(key)) continue;
    restored.push(item);
    seen.add(key);
  }

  if (restored.length === 0) return;

  QueueController.replaceFluidZone(
    restored.map((i) => i.track ?? i),
    { committedTail: COMMITTED_TAIL },
  );

  const label = String(fresh.ambientMoodLabel || fresh.timeOfDayLabel || 'calmer air');
  ellaRadioSessionRefs.onAmbientReweave?.(_ellaRevertLine(label), 'revert');

  console.log('[radioReweave] ambient_revert_restore', { restored: restored.length, snapshot: snap.signature });
}

function _momentReason(
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
): 'weather_shift' | 'time_shift' | 'combined_shift' {
  const weatherChanged = prev.weatherCondition !== next.weatherCondition;
  const timeChanged = prev.timeOfDayLabel !== next.timeOfDayLabel;
  if (weatherChanged && timeChanged) return 'combined_shift';
  if (weatherChanged) return 'weather_shift';
  return 'time_shift';
}

async function _pollAmbientReweave(force: boolean): Promise<void> {
  if (!isRadioRefillActive() || !_ambientAware()) return;

  let fresh: Record<string, unknown>;
  try {
    fresh = await fetchFreshEllaContinueOpts({ activeSession: true });
  } catch {
    return;
  }

  const sig = buildAmbientSignature(fresh);
  if (!_lastSignature) {
    _lastSignature = sig;
    return;
  }
  if (sig === _lastSignature) {
    if (_snapshotStack.length > 0 && force) {
      await _applySignatureRevert(fresh);
    }
    return;
  }

  const prevSig = _lastSignature;
  const prevOpts = { ...fresh, ..._parseSignature(prevSig) };
  _lastSignature = sig;

  if (_snapshotStack.length > 0 && sig === _snapshotStack[_snapshotStack.length - 1]?.signature) {
    await _applySignatureRevert(fresh);
    return;
  }

  await _applySignatureShift(fresh, prevOpts, _momentReason(prevOpts, fresh));
}

function _parseSignature(sig: string): Record<string, unknown> {
  const [weatherCondition, timeOfDayLabel, ambientMoodLabel, ambientEnergy] = sig.split('|');
  return {
    weatherCondition: weatherCondition || undefined,
    timeOfDayLabel: timeOfDayLabel || undefined,
    ambientMoodLabel: ambientMoodLabel || undefined,
    ambientEnergy: ambientEnergy !== '' ? Number(ambientEnergy) : undefined,
  };
}

export function seedAmbientReweaveSignature(opts: Record<string, unknown>): void {
  _lastSignature = buildAmbientSignature(opts);
}
